/**
 * KOL traffic lands in the SAME utm scheme as the paid ads, so one source
 * breakdown segments both instead of KOLs being a parallel dimension:
 * `utm_source` is the channel, `utm_campaign` the specific initiative.
 *
 *   utm_source=kol  utm_medium=referral  utm_campaign=<slug>  utm_content=<partner name>
 *
 * The CMS bakes these into the shareable link and the `/i/<slug>` page
 * synthesizes the same bag when a shared link arrives without them, so both
 * paths are generated from the builders here rather than written twice.
 */
export const ARENA_KOL_UTM_SOURCE = "kol";
export const ARENA_KOL_UTM_MEDIUM = "referral";

/**
 * Bound on any attribution value. Every one of them originates in a URL the
 * visitor controls and ends up in Mixpanel event and people properties, so the
 * arena capture store and the API sign-in schema both reject values past this
 * length or carrying control characters (they corrupt log lines and CSV exports).
 */
export const ATTRIBUTION_VALUE_MAX_LENGTH = 200;

// eslint-disable-next-line no-control-regex -- matching control chars in order to reject them
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/;
// eslint-disable-next-line no-control-regex -- as above, for stripping
const CONTROL_CHARS_GLOBAL = /[\u0000-\u001F\u007F-\u009F]/g;

/** The rule every attribution value must satisfy to survive capture. */
export function isUsableAttributionValue(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= ATTRIBUTION_VALUE_MAX_LENGTH &&
    !CONTROL_CHARS.test(value)
  );
}

/**
 * Free text (a partner display name) made to satisfy {@link isUsableAttributionValue}.
 * Null when nothing usable survives — the caller then omits the param rather
 * than emitting one the capture layer would silently drop.
 */
export function toAttributionValue(value: string | null | undefined): string | null {
  const clean = (value ?? "").replace(CONTROL_CHARS_GLOBAL, "").trim().slice(0, ATTRIBUTION_VALUE_MAX_LENGTH).trim();

  return clean.length > 0 ? clean : null;
}

/**
 * The utm bag for a partner ticket link. `utm_content` is dropped rather than
 * emitted empty when the partner name is unknown (an unreadable or closed link).
 */
export function buildArenaKolUtmParams(slug: string, partnerName?: string | null): Record<string, string> {
  const content = toAttributionValue(partnerName);

  return {
    utm_source: ARENA_KOL_UTM_SOURCE,
    utm_medium: ARENA_KOL_UTM_MEDIUM,
    utm_campaign: slug,
    ...(content ? { utm_content: content } : {}),
  };
}

/**
 * The bag as a query string. Percent-encoded rather than form-encoded: these
 * links are pasted into chat apps and bios, where a `+` for a space survives as
 * a literal plus often enough to matter.
 */
export function buildArenaKolUtmQuery(slug: string, partnerName?: string | null): string {
  return Object.entries(buildArenaKolUtmParams(slug, partnerName))
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
}
